import { useState, useEffect, useCallback } from 'react'
import { Col, Row, Container } from 'reactstrap'
import { Menu } from './Menu'
import { makeGetRequest, makePutRequest, makePaymentsGetRequest } from 'utils/api'
import { toOrganization, fromOrganization } from './serializers'
import { Details } from './Details'
import { Users } from './Users'
import { Templates } from './Templates'
import { Currency } from './Currency'
import { Opportunity } from './Opportunity'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { useDispatch } from 'react-redux'
import { Pipeline } from 'components/ideal-core-ui'
import { Imports } from './Imports'
import { Exports } from './Exports'
import { EmailSettings } from './EmailSettings'
import { Subscription } from './Subscription'
import { Company } from './Company'
import { LeadsModule } from './LeadsModule'
import { Contact } from './Contact'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'
import { isFreeTrial } from './helpers'
import { PageTitle } from 'components/common/PageTitle'
import { TimeTrackerModule } from './TimeTrackerModule'

const Organization = () => {
  const location = useLocation()
  const search = queryString.parse(location.search)
  const reduxDispatch = useDispatch()
  const [page, setPage] = useState(search.menu || 'details')
  const [details, setDetails] = useState({
    departments: [],
    email: '',
    id: '',
    interest: '',
    name: '',
    shortName: '',
    size: '',
    type: '',
    currency: {},
    phone: '',
    address: '',
    address2: '',
    street2: '',
    city: '',
    state: '',
    zipcode: '',
    dataExportSettings: {
      enabled: false,
      recipients: null
    },
    dealSettings: {
      sendApprovalEmail: false,
      wonDealEmail: false,
      checkOpportunityHealth: false,
      opportunityHealthManualOverride: false,
      showProjectStatus: false,
      showPublicPrivate: false,
      showInvitedToBid: false,
      teamAddEmail: false,
      filters: {},
      projectNumberDealStatuses: []
    },
    emailSettings: {
      name: '',
      domain: '',
      port: '',
      mode: null,
      includeDefaultEmailFooter: true
    },
    numberAutomations: [],
    emailIntegration: '',
    registeredOn: null,
    emailTC: {
      active: false,
      content: ''
    },
    stripeId: ''
  })
  const [users, setUsers] = useState([])
  // const [size, setSize] = useState('')
  const [departments, setDepartments] = useState([])
  const [isTrial, setIsTrial] = useState(false)
  const [subscriptionInfo, setSubscriptionInfo] = useState({ plan: 'FREE' })

  useEffect(() => {
    ;(async () => {
      const response = await makeGetRequest('/v1/organization')
      setDetails(toOrganization(response))
      setUsers(response.users)
      // setSize(response.size)
      setDepartments(response.departments)
    })()
  }, [])

  const getSubscriptionInfo = useCallback(async () => {
    try {
      const result = await makePaymentsGetRequest('/v1/subscriptions')
      setIsTrial(isFreeTrial(result.trialEndDate))
      setSubscriptionInfo(result)
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'An error occurred while fetching your subscription. Please contact support',
          level: 'error'
        }
      })
    }
  }, [reduxDispatch])

  useEffect(() => {
    ;(async () => {
      getSubscriptionInfo()
    })()
  }, [getSubscriptionInfo])

  const saveDetails = async (details, cb) => {
    try {
      const response = await makePutRequest('/v1/organization', fromOrganization(details))
      setDetails(toOrganization(response))
      setDepartments(response.departments)
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: 'Organization updated successfuly',
          level: 'success'
        }
      })
      cb && cb()
    } catch (e) {
      reduxDispatch({
        type: PUSH_NOTIFICATION,
        payload: {
          title: e.response?.data?.message || 'An error occurred. Please contact support',
          level: 'error'
        }
      })
    }
  }

  return (
    <>
      <PageTitle view="Settings" />
      <Container>
        <Row>
          <Col lg="2">
            <Menu page={page} setPage={setPage} />
          </Col>
          <Col lg="10">
            {page === 'details' && <Details saveDetails={saveDetails} details={details} />}
            {page === 'users' && (
              <Users
                organizationId={details.id}
                departments={departments}
                users={users || []}
                setUsers={setUsers}
                isFreeTrial={isTrial}
                subscriptionPlan={subscriptionInfo.plan}
                getSubscriptionInfo={getSubscriptionInfo}
              />
            )}
            {page === 'templates' && <Templates />}
            {page === 'currency' && <Currency saveDetails={saveDetails} details={details} />}
            {page === 'timeTracker' && (
              <TimeTrackerModule
                organizationId={details.id}
                iDealUsers={(users && users.filter(u => u.active)) || []}
                setIdealUsers={setUsers}
              />
            )}
            {page === 'pipeline' && (
              <Pipeline
                url="/v1/dealstatus"
                texts={{
                  addButtonText: 'Add New Pipeline Stage',
                  title: 'Current Pipeline Stages'
                }}
                admin
              />
            )}
            {page === 'leads' && <LeadsModule />}
            {page === 'opportunity' && <Opportunity saveDetails={saveDetails} details={details} />}
            {page === 'imports' && <Imports />}
            {page === 'exports' && <Exports details={details} saveDetails={saveDetails} />}
            {page === 'email' && <EmailSettings details={details} saveDetails={saveDetails} />}
            {page === 'subscription' && (
              <Subscription
                users={users || []}
                stripeId={details.stripeId}
                isFreeTrial={isTrial}
                subscriptionInfo={subscriptionInfo}
              />
            )}
            {page === 'company' && <Company />}
            {page === 'contact' && <Contact />}
          </Col>
        </Row>
      </Container>
    </>
  )
}

export default Organization
